import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useRole } from "./RoleContext";

export type Pathway = "learning" | "assessment" | null;

interface PathwayContextType {
  currentPathway: Pathway;
  setCurrentPathway: (pathway: Pathway) => void;
  needsPathwayChoice: boolean;
}

const PathwayContext = createContext<PathwayContextType | undefined>(undefined);

export function PathwayProvider({ children }: { children: ReactNode }) {
  const { userRole } = useRole();
  const [currentPathway, setCurrentPathway] = useState<Pathway>(() => {
    const stored = localStorage.getItem("current-pathway");
    return stored as Pathway || null;
  });

  // Parents and students only see assessment pathway - no choice needed
  const isAssessmentOnlyRole = userRole === "parent" || userRole === "student";
  
  // Automatically set pathway for assessment-only roles
  useEffect(() => {
    if (isAssessmentOnlyRole && currentPathway !== "assessment") {
      setCurrentPathway("assessment");
      localStorage.setItem("current-pathway", "assessment");
    }
  }, [isAssessmentOnlyRole, currentPathway]);

  const handleSetPathway = (pathway: Pathway) => {
    setCurrentPathway(pathway);
    if (pathway) {
      localStorage.setItem("current-pathway", pathway);
    } else {
      localStorage.removeItem("current-pathway");
    }
  };

  // Needs choice page if not assessment-only role and no pathway selected
  const needsPathwayChoice = !isAssessmentOnlyRole && !currentPathway;

  return (
    <PathwayContext.Provider value={{ 
      currentPathway: isAssessmentOnlyRole ? "assessment" : currentPathway, 
      setCurrentPathway: handleSetPathway,
      needsPathwayChoice
    }}>
      {children}
    </PathwayContext.Provider>
  );
}

export function usePathway() {
  const context = useContext(PathwayContext);
  if (context === undefined) {
    throw new Error("usePathway must be used within a PathwayProvider");
  }
  return context;
}
